import { useState } from "react";
import PropTypes from "prop-types";

function FareDetails({ fareQuoteData }) {
  const fare = fareQuoteData?.Results?.Fare;
  const fareBreakdown = fareQuoteData?.Results?.FareBreakdown || [];
  const finalTotalFare = fareQuoteData?.final_total_fare_with_taxes || fare?.PublishedFare || 0;

  const [showTaxDetails, setShowTaxDetails] = useState(false);

  if (!fare) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
        <div className="text-center text-gray-500 font-medium">Loading fare details...</div>
      </div>
    );
  }

  const totalTravellers = fareBreakdown.reduce((sum, item) => sum + item.PassengerCount, 0);

  const getPassengerLabelPlural = (type, count) => {
    switch (type) {
      case 1: return count > 1 ? "Adults" : "Adult";
      case 2: return count > 1 ? "Children" : "Child";
      case 3: return count > 1 ? "Infants" : "Infant";
      default: return "Passenger";
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-6 lg:sticky lg:top-20 w-full lg:w-[360px] border border-gray-100">
      {/* TOTAL PAYABLE SECTION */}
      <div className="mb-2">
        <h4 className="text-[14px] font-bold text-gray-500 tracking-wider mb-2">TOTAL PAYABLE</h4>
        <div className="text-[30px] font-black text-[#FF5B15] leading-none mb-2">
          ₹ {finalTotalFare.toLocaleString()}
        </div>
        <div className="text-[16px] text-gray-500 font-medium">
          {totalTravellers} Traveller{totalTravellers > 1 ? 's' : ''}
        </div>
      </div>

      <div className="w-full h-[1px] bg-gray-200 mb-3"></div>

      {/* FARE BREAKDOWN SECTION */}
      <div className="mb-3">
        <h5 className="text-[18px] font-semibold text-[#2d3e50] mb-5">Fare Breakdown</h5>

        <div className="space-y-6">
          {fareBreakdown.map((item, idx) => {
            const count = item.PassengerCount;
            const totalForCategory = item.BaseFare + item.Tax;
            const perPaxTotal = Math.round(totalForCategory / count);

            return (
              <div key={idx} className="flex justify-between items-center group">
                <div className="text-[17px] font-semibold text-[#2d3e50]">
                  {getPassengerLabelPlural(item.PassengerType, count)} ({count})
                </div>
                <div className="text-right flex items-center gap-2">
                  <span className="text-[16px] text-gray-500 font-medium">
                    ₹{perPaxTotal.toLocaleString()} × {count} =
                  </span>
                  <span className="text-[17px] font-semibold text-[#2d3e50] whitespace-nowrap">
                    ₹ {totalForCategory.toLocaleString()}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Discount if exists */}
          {fare.Discount > 0 && (
            <div className="flex justify-between items-center group">
              <div className="text-[17px] font-bold text-green-600">Discount</div>
              <div className="text-[17px] font-bold text-green-600">
                -₹ {fare.Discount.toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </div>
      {(fareQuoteData?.rc_platform_fee > 0) && (
        <div className="space-y-3 mb-3 pt-2 border-t border-gray-200">
          <div className="flex justify-between text-[15px] font-medium text-gray-600">
            <span>Platform Service Fee</span>
            <span>₹ {fareQuoteData.rc_platform_fee.toLocaleString()}</span>
          </div>
          {fareQuoteData.rc_gst_rate > 0 && (
            <div className="flex justify-between text-[15px] font-medium text-gray-600">
              <span>GST ({fareQuoteData.rc_gst_rate}%)</span>
              <span>₹ {((fareQuoteData.rc_platform_fee * fareQuoteData.rc_gst_rate) / 100).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}

      <div className="w-full h-[1px] bg-gray-200 mb-3"></div>

      {/* TOTAL FARE SUMMARY */}
      <div className="flex justify-between items-center mb-8">
        <div className="text-[18px] font-bold text-[#2d3e50]">Total Fare</div>
        <div className="text-[20px] font-bold text-[#2d3e50]">
          ₹ {finalTotalFare.toLocaleString()}
        </div>
      </div>

      {/* VIEW TAX DETAILS TOGGLE */}
      <div className="bg-[#f8fbff] -mx-8 -mb-8 mt-4 rounded-b-xl overflow-hidden border-t border-gray-100">
        {/* <button
          onClick={() => setShowTaxDetails(!showTaxDetails)}
          className="w-full p-5 flex items-center justify-between text-[#2d3e50] font-bold hover:bg-[#f0f7ff] transition-colors"
        >
          <span className="text-[16px] text-blue-600 group flex items-center hover:underline">
            View tax details
            <svg
              className={`ml-2 w-4 h-4 transition-transform ${showTaxDetails ? "rotate-90" : ""}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </button> */}

        {showTaxDetails && (
          <div className="px-8 pb-8 pt-2 space-y-3 bg-[#f8fbff]">
            <div className="flex justify-between text-[15px] font-medium text-gray-600">
              <span>Airline Taxes & Fees</span>
              <span>₹ {(fareBreakdown.reduce((s, i) => s + i.Tax, 0)).toLocaleString()}</span>
            </div>


          </div>
        )}
      </div>
    </div>
  );
}

FareDetails.propTypes = {
  fareQuoteData: PropTypes.object,
};

export default FareDetails;
